import { AppUser } from '../types';
import { TIER_PRIVILEGES, LEGEND_TIER_LEVEL, type ModerationAction } from '../constants';
import type { PremiumFeatureKey } from '../components/PremiumNudge';

export const isWithinTrialPeriod = (user?: Pick<AppUser, 'creationTimestamp'> | null): boolean => {
  if (!user?.creationTimestamp) return false;
  const sevenDaysInMs = 7 * 24 * 60 * 60 * 1000;
  return (Date.now() - user.creationTimestamp) < sevenDaysInMs;
};

export const isAdminUser = (
  user?: Pick<AppUser, 'email' | 'isAdmin' | 'adminClaim' | 'role' | 'roles'> | null,
  isDev = false
): boolean => {
  if (isDev) return true;
  if (!user) return false;
  const roleValue = String((user as any).role || '').toLowerCase();
  const roleList = Array.isArray((user as any).roles)
    ? (user as any).roles.map((r: any) => String(r).toLowerCase())
    : [];
  return (
    user.isAdmin === true ||
    user.adminClaim === true ||
    roleValue === 'admin' ||
    roleList.includes('admin')
  );
};

/** Tester gjelder bare så lenge tester_valid_until ikke er passert. */
export const isActiveTester = (
  user?: Pick<AppUser, 'isTester' | 'tester_valid_until'> | null
): boolean => {
  if (!user?.isTester) return false;
  const validUntil = user.tester_valid_until;
  if (!validUntil) return true;
  const validMs = typeof validUntil === 'number'
    ? validUntil
    : (typeof validUntil?.toMillis === 'function'
      ? validUntil.toMillis()
      : (typeof validUntil?.seconds === 'number' ? validUntil.seconds * 1000 : null));
  if (validMs === null) return true;
  return Date.now() < validMs;
};

/**
 * GA-flagg for reise-copiloten. PÅ først i byggemiljøet der egen Overpass er satt opp
 * (`VITE_COPILOT_GA=true` sammen med `VITE_OVERPASS_URL`) — se docs/self-hosted-routing.md.
 * Av (default) → copiloten er fortsatt testfunksjon (kun admin + aktive testere).
 */
export const COPILOT_GA = (import.meta as any).env?.VITE_COPILOT_GA === 'true';

/**
 * Tilgang til reise-copiloten. Admin og aktive testere har ALLTID tilgang. Ved GA
 * (`gaEnabled`) får også betalende (premium) tilgang — copiloten blir en premium-funksjon.
 * Backend speiler dette: planTripWithCopilot bruker requirePaidAccess ved GA.
 */
export const hasCopilotAccess = (
  user?: Pick<AppUser, 'email' | 'isAdmin' | 'adminClaim' | 'role' | 'roles' | 'isTester' | 'tester_valid_until' | 'subscriptionStatus' | 'paidUntil' | 'tier' | 'creationTimestamp'> | null,
  gaEnabled: boolean = COPILOT_GA,
): boolean => {
  if (isAdminUser(user) || isActiveTester(user)) return true;
  return gaEnabled ? hasPaidAccess(user) : false;
};

/**
 * GA-flagg for Reisefilm (reise-replay). Av (default) → testfunksjon: kun
 * admin + aktive testere ser «Reisefilm»-knappen. Ved GA åpnes den for alle
 * innloggede (featuren er bevisst gratis — hver delt video markedsfører appen).
 */
export const REPLAY_GA = (import.meta as any).env?.VITE_REPLAY_GA === 'true';

/**
 * GA-flagg for verving (#3 Reciprocity). Av (default) → invite-UI skjult og ingen
 * ?ref-claim kjøres. Speiler server-flagget system/referral_config.enabled: BEGGE må
 * på for at loopen skal virke live (klient viser UI, server granter). Skru på først når
 * svindel-modellen er gjennomgått. Se docs/plan-ux-principles-retention.md.
 */
export const REFERRAL_GA = (import.meta as any).env?.VITE_REFERRAL_GA === 'true';

/** Tilgang til Reisefilm. Admin og aktive testere alltid; alle ved GA. */
export const hasReplayAccess = (
  user?: Pick<AppUser, 'email' | 'isAdmin' | 'adminClaim' | 'role' | 'roles' | 'isTester' | 'tester_valid_until'> | null,
  gaEnabled: boolean = REPLAY_GA,
): boolean => {
  if (isAdminUser(user) || isActiveTester(user)) return true;
  return gaEnabled && !!user;
};

export const hasPaidAccess = (
  user?: Pick<AppUser, 'email' | 'isAdmin' | 'adminClaim' | 'role' | 'roles' | 'subscriptionStatus' | 'paidUntil' | 'isTester' | 'tier' | 'creationTimestamp'> | null,
  isDev = false,
  featureKey?: PremiumFeatureKey
): boolean => {
  if (!user) return false;
  if (
    isAdminUser(user, isDev) ||
    user.subscriptionStatus === 'paid' ||
    (typeof user.paidUntil === 'number' && Date.now() < user.paidUntil) ||
    isActiveTester(user) ||
    isWithinTrialPeriod(user)
  ) return true;
  // Tier-basert tilgang for spesifikke features
  if (featureKey && hasTierAccess(user, featureKey)) return true;
  return false;
};

/** Sjekk om bruker har Premium-abonnement (for Android Auto / CarPlay). */
export const hasPremiumAccess = (
  user?: Pick<AppUser, 'email' | 'isAdmin' | 'adminClaim' | 'role' | 'roles' | 'subscriptionStatus' | 'subscriptionPlan' | 'paidUntil' | 'isTester' | 'tier'> | null,
  isDev = false
): boolean => {
  if (!user) return false;
  if (isAdminUser(user, isDev)) return true;
  if (isActiveTester(user)) return true;
  // Legende (tier 5) beholder premium-funksjoner (Android Auto / CarPlay) gratis
  // som belønning for bidrag. Inert så lenge AUTO_IS_FREE gir alle tilgang
  // (se useAndroidAuto); slår først inn når betalingsmuren skrus på.
  if ((user.tier || 0) >= LEGEND_TIER_LEVEL) return true;
  return user.subscriptionPlan === 'premium' && (
    user.subscriptionStatus === 'paid' ||
    (typeof user.paidUntil === 'number' && Date.now() < user.paidUntil)
  );
};

/** Sjekk om brukerens tier gir tilgang til en spesifikk premium-funksjon. */
export const hasTierAccess = (
  user?: Pick<AppUser, 'tier'> | null,
  featureKey?: PremiumFeatureKey
): boolean => {
  if (!user || !featureKey) return false;
  const tier = user.tier || 0;
  const privileges = TIER_PRIVILEGES[tier];
  if (!privileges) return false;
  return privileges.unlockedFeatures.includes(featureKey);
};

/** Sjekk om bruker har modereringsrettigheter for en gitt handling. */
export const hasModerationAccess = (
  user?: Pick<AppUser, 'email' | 'isAdmin' | 'adminClaim' | 'role' | 'roles' | 'tier'> | null,
  action?: ModerationAction,
  isDev = false
): boolean => {
  if (!user) return false;
  if (isAdminUser(user, isDev)) return true;
  if (!action) return false;
  const tier = user.tier || 0;
  const privileges = TIER_PRIVILEGES[tier];
  if (!privileges) return false;
  switch (action) {
    case 'category_suggestions': return privileges.canApproveCategorySuggestions;
    case 'route_suggestions': return privileges.canApproveRouteSuggestions;
    case 'spot_deletions': return privileges.canApproveSpotDeletions;
    default: return false;
  }
};
